import {
  ComponentProps,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { OkButton } from 'src/components/Buttons/OkButton';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { useEventItemLoadingDisplay } from 'src/hooks/useEventItemLoadingDisplay';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { isSuccess } from 'src/utils/errorUtils';
import { hasChanged, posChangedField } from 'src/utils/posFieldUtils';
import {
  getPosManagedMarketplaceSaleInputFromListing,
  getPosManagedMarketplaceSaleInputFromSale,
  getSaleDetailsRelativeUrl,
} from 'src/utils/saleUtils';
import { EntityWithRealTickets } from 'src/utils/ticketUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  ApiException,
  Event,
  Feature,
  ListingDetails,
  PosUiActionResult,
  SaleClient,
  SaleDetails,
} from 'src/WebApiController';

import { CancellableFormFooter } from '../common';
import { CancellableFormHeader } from '../common/CancellableFormHeader';
import { ConnectedEventEntityHeader } from '../common/EventEntityHeader';
import { modalDetails } from '../common/Modals.css';
import { Summary } from '../common/Summary';
import { ModalProps } from '../Modal';
import {
  ModalBody,
  ModalBodyHeaderContainer,
  ModalFooter,
} from '../Modal/Modal.styled';
import { ManagedMarketplaceSaleFormInput } from './ManagedMarketplaceSale.types';
import { ManagedMarketplaceSaleBody } from './ManagedMarketplaceSaleBody';

type ManagedMarketplaceSaleProps = {
  isDonation?: boolean;
  cancelTo?: ModalProps;
};

export const ManagedMarketplaceSale = ({
  cancelTo,
  isDonation,
}: ManagedMarketplaceSaleProps) => {
  const { posEntity, event, posEntityDisplayId, posEntityId } =
    useActivePosEntityContext<EntityWithRealTickets>();

  const hasMarketplaceSaleAvoidIdPrefillFeature = useUserHasFeature(
    Feature.MarketplaceSaleAvoidIdPrefill
  );
  const ihdTimezoneFix = useUserHasFeature(Feature.IhdTimezoneFix);

  const listing =
    posEntity?.entityType === ActionOutboxEntityType.Listing
      ? (posEntity as ListingDetails)
      : undefined;
  const sale =
    posEntity?.entityType === ActionOutboxEntityType.Sale
      ? (posEntity as SaleDetails)
      : undefined;

  const methods = useForm<ManagedMarketplaceSaleFormInput>({
    defaultValues:
      listing?.tickets?.length && event
        ? getPosManagedMarketplaceSaleInputFromListing(
            listing,
            event,
            hasMarketplaceSaleAvoidIdPrefillFeature,
            isDonation,
            ihdTimezoneFix
          )
        : sale?.tickets?.length && event
          ? getPosManagedMarketplaceSaleInputFromSale(sale, event)
          : undefined,
  });

  useEffect(() => {
    if (
      methods.formState.defaultValues === undefined &&
      (listing?.tickets?.length || sale?.tickets?.length) &&
      event
    ) {
      methods.reset(
        listing?.tickets?.length
          ? getPosManagedMarketplaceSaleInputFromListing(
              listing,
              event,
              hasMarketplaceSaleAvoidIdPrefillFeature,
              isDonation,
              ihdTimezoneFix
            )
          : getPosManagedMarketplaceSaleInputFromSale(sale!, event)
      );
    }
  }, [
    sale,
    methods,
    event,
    listing,
    hasMarketplaceSaleAvoidIdPrefillFeature,
    isDonation,
    ihdTimezoneFix,
  ]);

  // Initialize the private per-unit net-proceeds rate from the default proceeds and
  // quantity once they're available. MarketplaceSaleInfoSection keeps it in sync when
  // the user edits the proceeds; TicketSection reads it when the quantity changes.
  const defaultTotalNetProceeds = methods.watch('totalNetProceeds')?.value;
  const defaultQuantitySold = methods.watch('quantitySold')?.value;
  useEffect(() => {
    if (
      methods.getValues('netProceedsPerCount') == null &&
      defaultTotalNetProceeds != null &&
      defaultQuantitySold != null &&
      defaultQuantitySold > 0
    ) {
      methods.setValue(
        'netProceedsPerCount',
        defaultTotalNetProceeds / defaultQuantitySold
      );
    }
  }, [methods, defaultTotalNetProceeds, defaultQuantitySold]);

  return (
    <FormProvider {...methods}>
      <ManagedSaleContent
        {...methods}
        cancelTo={cancelTo}
        listing={listing}
        sale={sale}
        posEntityDisplayId={posEntityDisplayId ?? posEntityId?.toString()}
        event={event}
        isDonation={isDonation}
      />
    </FormProvider>
  );
};

export const ManagedSaleContent = ({
  listing,
  isDonation,
  sale,
  posEntityDisplayId,
  event,
  cancelTo,
  formState,
  handleSubmit,
  watch,
  getValues,
  setError,
}: Omit<
  ComponentProps<typeof FormProvider<ManagedMarketplaceSaleFormInput, unknown>>,
  'children'
> &
  ManagedMarketplaceSaleProps & {
    listing?: ListingDetails;
    sale?: SaleDetails;
    posEntityDisplayId?: string;
    event?: Event | null;
    isDonation?: boolean;
  }) => {
  const { showErrorDialog, genericError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { setModal, closeModal } = useContext(ModalContext);
  const { setActivePosEntity } = useActivePosEntityContext();

  const { isLoading: entityIsLoading, loadingState } =
    useEventItemLoadingDisplay<SaleDetails>(
      FormatContentId.LoadingSaleId,
      FormatContentId.SearchingForSaleId,
      FormatContentId.CouldNotFindSaleId,
      posEntityDisplayId
    );

  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit = useCallback(
    async (saleFormValue: ManagedMarketplaceSaleFormInput) => {
      setIsLoading(true);
      // netProceedsPerCount is a client-only field — never send it to the backend.
      const { netProceedsPerCount: _netProceedsPerCount, ...saleInput } =
        saleFormValue;
      tryInvokeApi(
        async () => {
          const client = new SaleClient(activeAccountWebClientConfig);
          let result: PosUiActionResult;
          if (isDonation) {
            result = await client.mergeDonationSaleForListingId(
              saleInput.listingId,
              saleInput
            );
          } else {
            result = await client.mergePosManagedMarketplaceSaleForListingId(
              saleInput.listingId,
              saleInput
            );
          }

          if (isSuccess(result)) {
            if (sale) {
              await setActivePosEntity(sale.id, sale.idOnMkp, true);
            } else if (result.entityId) {
              navigate(getSaleDetailsRelativeUrl(result.entityId));
            }
            if (cancelTo) {
              setModal(cancelTo);
            } else {
              closeModal(true);
            }
          } else {
            showErrorDialog(
              'SaleClient.mergePosManagedMarketplaceSale',
              {
                message: result.message ?? genericError,
                status: result.status!,
              } as ApiException,
              {
                trackErrorData: saleFormValue,
              }
            );
          }
        },
        (error) => {
          showErrorDialog('SaleClient.mergePosManagedMarketplaceSale', error, {
            trackErrorData: saleFormValue,
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      cancelTo,
      closeModal,
      genericError,
      isDonation,
      navigate,
      sale,
      setActivePosEntity,
      setModal,
      showErrorDialog,
    ]
  );

  const hasChanges = useCallback(
    (saleFormValue: ManagedMarketplaceSaleFormInput) => {
      if (
        hasChanged(
          saleFormValue.marketplaceSaleId,
          formState.defaultValues?.marketplaceSaleId
        ) ||
        hasChanged(
          saleFormValue.saleStatus,
          formState.defaultValues?.saleStatus
        ) ||
        hasChanged(
          saleFormValue.buyerEmail,
          formState.defaultValues?.buyerEmail
        ) ||
        hasChanged(
          saleFormValue.buyerName,
          formState.defaultValues?.buyerName
        ) ||
        hasChanged(
          saleFormValue.buyerPhone,
          formState.defaultValues?.buyerPhone
        ) ||
        hasChanged(
          saleFormValue.currencyCode,
          formState.defaultValues?.currencyCode
        ) ||
        hasChanged(
          saleFormValue.inHandDate,
          formState.defaultValues?.inHandDate
        ) ||
        hasChanged(
          saleFormValue.quantitySold,
          formState.defaultValues?.quantitySold
        ) ||
        hasChanged(saleFormValue.saleDate, formState.defaultValues?.saleDate) ||
        hasChanged(saleFormValue.section, formState.defaultValues?.section) ||
        hasChanged(saleFormValue.row, formState.defaultValues?.row) ||
        hasChanged(saleFormValue.seatFrom, formState.defaultValues?.seatFrom) ||
        hasChanged(saleFormValue.seatTo, formState.defaultValues?.seatTo) ||
        hasChanged(
          saleFormValue.ticketType,
          formState.defaultValues?.ticketType
        ) ||
        hasChanged(
          saleFormValue.totalNetProceeds,
          formState.defaultValues?.totalNetProceeds
        ) ||
        hasChanged(
          saleFormValue.internalNotes,
          formState.defaultValues?.internalNotes
        ) ||
        hasChanged(
          saleFormValue.paymentState,
          formState.defaultValues?.paymentState
        ) ||
        hasChanged(
          saleFormValue.fulfillerSellerUserId,
          formState.defaultValues?.fulfillerSellerUserId
        )
      ) {
        return true;
      }
      return false;
    },
    [formState.defaultValues]
  );

  const requiredMsg = useContent(ContentId.Required);
  const inhandDateMustBeforeEventDate = useContent(
    ContentId.InHandDateMustBeBeforeEventDate
  );

  const onSubmitHandler = useCallback(() => {
    const formValues = getValues();
    if (!hasChanges(formValues)) return;

    if (!formValues.saleDate) {
      setError('saleDate', { message: requiredMsg }, { shouldFocus: true });
    }

    if (!formValues.totalNetProceeds?.value) {
      formValues.totalNetProceeds = posChangedField(0);
    }

    if (!formValues.inHandDate?.value) {
      setError('inHandDate', { message: requiredMsg }, { shouldFocus: true });
    } else if (
      new Date(formValues.inHandDate.value) >= new Date(event!.dates.start!)
    ) {
      setError(
        'inHandDate',
        { message: inhandDateMustBeforeEventDate },
        { shouldFocus: true }
      );
    }
    if ((formValues.quantitySold?.value ?? 0) <= 0) {
      setError('quantitySold', { message: requiredMsg }, { shouldFocus: true });
    }

    if (listing?.tickets?.find((t) => !!t.seat)) {
      // If the listing tickets has seats - so better the sale
      if (!formValues.seatFrom?.value || !formValues.seatTo?.value) {
        setError('seatFrom', { message: requiredMsg }, { shouldFocus: true });
      }
    }

    handleSubmit(onSubmit)();
  }, [
    getValues,
    hasChanges,
    event,
    listing?.tickets,
    handleSubmit,
    onSubmit,
    setError,
    requiredMsg,
    inhandDateMustBeforeEventDate,
  ]);

  const titleContentId = useMemo(() => {
    if (sale) {
      return ContentId.EditOfflineSale;
    }
    return isDonation ? ContentId.DonateListing : ContentId.AddOfflineSale;
  }, [isDonation, sale]);

  if (!sale && !listing) {
    // If we're looking for an active Sale, and there isn't one, return loading state
    return loadingState;
  }

  const formHasChanges = hasChanges(watch());

  return (
    <>
      <CancellableFormHeader
        cancelTo={cancelTo}
        disabled={isLoading || formState.isSubmitting}
        showDialogOnCancel={formHasChanges}
      >
        <ConnectedEventEntityHeader title={<Content id={titleContentId} />} />
      </CancellableFormHeader>

      <ModalBody>
        <div className={modalDetails}>
          {event && (listing || sale) && (
            <ModalBodyHeaderContainer>
              <Summary event={event} posEntity={sale || listing} />
            </ModalBodyHeaderContainer>
          )}
          {entityIsLoading || !(listing || sale) ? (
            <PosSpinner />
          ) : (
            <ManagedMarketplaceSaleBody
              disabled={isLoading}
              listing={listing}
              sale={sale}
              event={event}
              isDonation={isDonation}
            />
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <CancellableFormFooter
          cancelTo={cancelTo}
          disabled={isLoading || entityIsLoading}
          showDialogOnCancel={formHasChanges}
        >
          <OkButton
            data-testid="sale-save-button"
            onClick={onSubmitHandler}
            disabled={isLoading || entityIsLoading || !formHasChanges}
            textContentId={ContentId.Save}
          />
        </CancellableFormFooter>
      </ModalFooter>
    </>
  );
};
